// Step 7.1 commit 2 / AC246: schedule.ts is now a thin compatibility
// wrapper over lib/delivery/next-deliveries. Function shapes preserve
// the Step-5/6 contract used by DeliveryBanner, DeliveryTimelineCard,
// HarvestArrivalRow, and the Dashboard page. The cutoff math used to
// approximate "delivery_noon_UTC - 10h" (= 22:00 ET) which only
// matched spec by coincidence in EDT and drifted under EST; this
// version delegates to the ET-aware module so every surface agrees.

import {
  getNextDeliveries as canonicalNext,
  getCurrentWindow,
  getNextDelivery as canonicalNextDelivery,
} from "./next-deliveries";

export type DeliveryWindow = {
  nextMon: Date;
  nextWed: Date;
  cutoffForMon: Date;
  cutoffForWed: Date;
};

export type OrderWindowProgress = {
  elapsedMs: number;
  totalMs: number;
  pct: number;
};

export function getNextDeliveries(now: Date): DeliveryWindow {
  // Pull a window large enough to guarantee both a Mon and a Wed.
  const list = canonicalNext(now, { count: 6 });
  const mon = list.find((o) => o.weekday === 1);
  const wed = list.find((o) => o.weekday === 3);
  // Defensive: in the extreme case of an open-ended seasonal block
  // returning empty, fall back to one Mon + one Wed seven days out.
  // Practical: never hits because seasonal blocks max at ~16 days.
  return {
    nextMon: mon?.date ?? new Date(now.getTime() + 7 * 86400000),
    nextWed: wed?.date ?? new Date(now.getTime() + 14 * 86400000),
    cutoffForMon: mon?.cutoff ?? new Date(now.getTime()),
    cutoffForWed: wed?.cutoff ?? new Date(now.getTime()),
  };
}

export function getNextDeliveryForCountdown(now: Date): {
  delivery: Date;
  cutoff: Date;
  previousCutoff: Date;
} {
  const win = getCurrentWindow(now);
  if (!win) {
    // No upcoming delivery (would only happen mid-seasonal-block at end
    // of calendar) - return a placeholder that the UI can render
    // without crashing.
    return {
      delivery: new Date(now.getTime() + 7 * 86400000),
      cutoff: new Date(now.getTime() + 5 * 86400000),
      previousCutoff: new Date(now.getTime() - 1 * 86400000),
    };
  }
  return {
    delivery: win.current.date,
    cutoff: win.current.cutoff,
    previousCutoff: win.previousCutoff,
  };
}

export function getOrderWindowProgress(now: Date): OrderWindowProgress {
  const { cutoff, previousCutoff } = getNextDeliveryForCountdown(now);
  const totalMs = cutoff.getTime() - previousCutoff.getTime();
  const elapsedMs = Math.max(
    0,
    Math.min(totalMs, now.getTime() - previousCutoff.getTime()),
  );
  const pct = totalMs > 0 ? (elapsedMs / totalMs) * 100 : 0;
  return { elapsedMs, totalMs, pct };
}

export function computeHarvestAndArrival(
  now: Date,
  harvestCycleDays: number,
): { harvestDate: Date; arrivalDate: Date } {
  const next = canonicalNextDelivery(now);
  const arrivalDate =
    next?.date ?? new Date(now.getTime() + 7 * 86400000);
  const harvestDate = new Date(
    arrivalDate.getTime() - harvestCycleDays * 86400000,
  );
  return { harvestDate, arrivalDate };
}
